import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateOperationRequest, Operation, OperationConstraint, UpdateOperationRequest } from '@models/index';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  getOne,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { OperationsService } from './operations.service';

type OperationsState = {
  operations: Operation[];
  isLoading: boolean;
  selectedOperation: Operation | null;
  isSelectedOperationLoading: boolean;
  isOperationCreateLoading: boolean;
  isOperationConstraintsLoading: boolean;
};

const initialState: OperationsState = {
  operations: [],
  isLoading: false,
  selectedOperation: null,
  isSelectedOperationLoading: false,
  isOperationCreateLoading: false,
  isOperationConstraintsLoading: false,
};

const operationsDictionary: StateDictionary<Operation> = {
  entityList: 'operations',
  listLoading: 'isLoading',
  createLoading: 'isOperationCreateLoading',
  selectedEntity: 'selectedOperation',
  selectedEntityLoading: 'isSelectedOperationLoading',
  entityId: 'id',
  sortFn: (a: Operation, b: Operation) => a.name.localeCompare(b.name),
};

export const OperationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('operations-store'),
  withMethods((store, operationsService = inject(OperationsService), router = inject(Router)) => {
    createStateResetHandler<OperationsState>('/job-orchestrator/operations')(store, initialState);
    createCustomStateSetOnUrlHandler<OperationsState>('/job-orchestrator/operations', {
      selectedOperation: null,
    })(store);

    const repositoryMethods = {
      getAll: getAll<Operation>({
        store,
        service: operationsService,
        dictionary: operationsDictionary,
      }),

      getOne: getOne<Operation>({
        store,
        service: operationsService,
        dictionary: operationsDictionary,
      }),

      create: addEntity<Operation, CreateOperationRequest>({
        store,
        service: operationsService,
        dictionary: operationsDictionary,
        onSuccessCallback: (newOperation: Operation) => {
          patchState(store, { selectedOperation: newOperation });
          router.navigate(['job-orchestrator', 'operations', newOperation.id, 'details']);
        },
      }),

      update: updateEntity<Operation, UpdateOperationRequest>({
        store,
        service: operationsService,
        dictionary: operationsDictionary,
        onSuccessCallback: (updatedOperation: Operation) => {
          patchState(store, { selectedOperation: updatedOperation });
        },
      }),

      delete: deleteEntity<Operation>({
        store,
        service: operationsService,
        dictionary: operationsDictionary,
        onSuccessCallback: () => {
          repositoryMethods.getAll();
          router.navigate(['job-orchestrator', 'operations']);
        },
      }),

      updateConstraints: rxMethod<{ operationId: string; constraints: OperationConstraint[] }>(
        pipe(
          tap(() => patchState(store, { isOperationConstraintsLoading: true, isSelectedOperationLoading: true })),
          switchMap(({ operationId, constraints }) => {
            const previousOperation = store.selectedOperation();
            const previousOperations = store.operations();
            if (previousOperation) {
              const optimisticOperation = { ...previousOperation, constraints };
              patchState(store, {
                selectedOperation: optimisticOperation,
                operations: previousOperations.map(operation => (operation.id === operationId ? optimisticOperation : operation)),
              });
            }

            return operationsService.updateConstraints(operationId, constraints).pipe(
              tap((operation: Operation) => {
                patchState(store, {
                  selectedOperation: operation,
                  operations: store.operations().map(existing => (existing.id === operationId ? operation : existing)),
                  isOperationConstraintsLoading: false,
                  isSelectedOperationLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, {
                  selectedOperation: previousOperation,
                  operations: previousOperations,
                  isOperationConstraintsLoading: false,
                  isSelectedOperationLoading: false,
                });
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    };

    const navigationActions = {
      goToOperationActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['job-orchestrator', 'operations', id]);
        return result ?? false;
      },
    };

    return { ...repositoryMethods, ...navigationActions };
  }),
);

export interface OperationsStoreType {
  readonly operations: Signal<Operation[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedOperation: Signal<Operation | null>;
  readonly isSelectedOperationLoading: Signal<boolean>;
  readonly isOperationCreateLoading: Signal<boolean>;
  readonly isOperationConstraintsLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly create: (request: CreateOperationRequest) => void;
  readonly update: (params: { id: string; dto: UpdateOperationRequest }) => void;
  readonly delete: (id: string) => void;
  readonly updateConstraints: (params: { operationId: string; constraints: OperationConstraint[] }) => void;
  readonly goToOperationActions: (id: string) => Promise<boolean>;
}
